import React, { useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import { AdminStarRating, InterestStatsPanel, RatingsBreakdown } from "../../../components/DealFlow";
import { canApprove } from "../../../utils/dealFlowRoleUtils";
import {
  approveCampaignInitial,
  rejectCampaignInitial,
  updateCampaignType,
  fetchCampaignProducts,
} from "../../../utils/campaignViewActions";

/**
 * InitialApprovalActions — First-time approval workflow
 * Backend: update-campaign-byadmin (superAdmin.controller.js:1830)
 */
const InitialApprovalActions = ({
  canEdit,
  userInfo,
  campaign,
  campaignId,
  setLoading,
  ratingsData,
  myRating,
  onPrint,
}) => {
  const navigate = useNavigate();
  const [comment, setComment] = useState("");
  const [productsType, setProductsType] = useState([]);
  const [productId, setProductId] = useState();
  const [campaignType, setCampaignType] = useState("");

  const resolvedCampaignId = campaignId || campaign?.data?.[0]?.id;

  useEffect(() => {
    if (campaign?.data?.[0]?.type) {
      fetchCampaignProducts(campaign.data[0].type).then(setProductsType);
    }
  }, [campaign]);

  // QA 2026-07-28 F-1: no setLoading around these two. Both open a confirmation
  // dialog and await the operator's click; the page-level loader (z 99999) sat
  // above the Swal (z 1060) and swallowed it. `confirmAction` already renders
  // SweetAlert's own inline spinner for the network phase via preConfirm +
  // showLoaderOnConfirm, so nothing is lost.
  const handleApprove = async () => {
    const result = await approveCampaignInitial(resolvedCampaignId, {
      comment,
      productId,
    });
    if (result.success && result.navigateTo) navigate(result.navigateTo);
  };

  const handleReject = async () => {
    const result = await rejectCampaignInitial(resolvedCampaignId, { comment });
    if (result.success && result.navigateTo) navigate(result.navigateTo);
  };

  // Keeps its loader: updateCampaignType opens no dialog, so here the overlay
  // wraps only the network call - which is what it is for.
  const handleCampaignTypeSubmit = async () => {
    setLoading(true);
    await updateCampaignType(resolvedCampaignId, campaignType);
    setLoading(false);
  };

  return (
    <div className="left">
      <div className="title-topSection">
        <h1 className="title">Campaign Review</h1>
        <button onClick={onPrint} className="approveButton downloadbtn">
          Download
        </button>
      </div>

      {/* Star Rating */}
      {resolvedCampaignId && (
        <div style={{ marginBottom: "16px" }}>
          <AdminStarRating
            campaignId={resolvedCampaignId}
            initialRating={myRating?.rating || 0}
            initialNotes={myRating?.notes || ""}
          />
          <RatingsBreakdown ratings={ratingsData} loading={!ratingsData} />
          <InterestStatsPanel
            campaignId={resolvedCampaignId}
            currency={userInfo?.currency_type || "CAD"}
          />
        </div>
      )}

      {/* Product Type Selection */}
      {productsType.length > 0 && (
        <div className="detailItem" style={{ marginBottom: "12px" }}>
          <span className="itemKey">Product Type:</span>
          <select
            value={productId}
            onChange={(e) => setProductId(e.target.value)}
            className="itemValue"
          >
            <option value="">Select Product</option>
            {productsType.map((p) => (
              <option key={p.id} value={p.id}>
                {p.name || p.product_name}
              </option>
            ))}
          </select>
        </div>
      )}

      {/* Comment */}
      <div className="detailItem" style={{ marginBottom: "12px" }}>
        <span className="itemKey">Comment:</span>
        <textarea
          value={comment}
          onChange={(e) => setComment(e.target.value)}
          className="itemValue"
          rows={3}
          placeholder="Add a comment (optional)"
          style={{ width: "100%", padding: "8px" }}
        />
      </div>

      {/* Approve/Reject */}
      {canApprove() && canEdit !== false ? (
        <div style={{ marginBottom: "16px" }}>
          <p>You can click on Approve / Reject button below to respond.</p>
          <div style={{ display: "flex", gap: "10px", marginTop: "10px" }}>
            <button className="approveButton" onClick={handleApprove}>
              Approve
            </button>
            <button className="approveButton" onClick={handleReject} style={{ background: "#d33" }}>
              Reject
            </button>
          </div>
        </div>
      ) : (
        <p style={{ color: "#666", fontStyle: "italic" }}>
          As an Evaluator, you can rate campaigns but cannot approve or reject them.
        </p>
      )}

      {/* Campaign Type */}
      <div className="detailItem" style={{ marginTop: "12px" }}>
        <span className="itemKey">Campaign Type:</span>
        <input
          type="text"
          value={campaignType}
          onChange={(e) => setCampaignType(e.target.value)}
          className="itemValue"
          placeholder="Enter campaign type"
          style={{ padding: "6px", marginRight: "8px" }}
        />
        {canEdit !== false && (
        <button className="approveButton" onClick={handleCampaignTypeSubmit}>
          Update Type
        </button>
        )}
      </div>
    </div>
  );
};

export default InitialApprovalActions;
